import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { SearchParam } from '@common/ts/enums';
import useTranslation from '@/hooks/useTranslation';
import { useAnalysisSchemaStatus, useAnalysisRunProgress } from '@/reactQuery/researchAssistant';
import NonBlockingProgressStrip from '@/components/ui/NonBlockingProgressStrip';
import ComparisonHub from './ComparisonHub';
import ComparisonReport from './ComparisonReport';

interface CompareTabProps {
  analysisLanguage: string;
  onReRun?: (filterId?: string | null) => Promise<void>;
  isRerunLoading?: boolean;
}

function CompareTab({ analysisLanguage, onReRun, isRerunLoading }: CompareTabProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const comparisonId = searchParams.get(SearchParam.COMPARISON_ID);

  // Pre-flight: does this project have the rich analysis schema the
  // Compare feature needs? Check is project-level (no filter) because
  // Compare targets multiple filters and the gate applies to the base
  // analysis pipeline.
  // D.8: replaced OutdatedAnalysisDialog with inline notice. The dialog is
  // deleted; per-tab inline notice replaces the modal blocking behaviour.
  const { data: schemaStatus } = useAnalysisSchemaStatus(projectId, null);
  const { data: runProgress } = useAnalysisRunProgress(projectId);
  const isRunInFlight =
    !!runProgress && runProgress.phase !== 'idle' && runProgress.phase !== 'complete';

  const schemaOutdated = !!schemaStatus && !schemaStatus.current;

  if (comparisonId) {
    return <ComparisonReport comparisonId={comparisonId} analysisLanguage={analysisLanguage} />;
  }

  return (
    <>
      {/* D.8: per-tab inline notice when schema is outdated (replaces modal) */}
      {schemaOutdated && !isRunInFlight && (
        <div className="max-w-3xl mx-auto px-6 pt-6">
          <NonBlockingProgressStrip variant="banner" title={lang.get('msg.compareNeedsRerun')} />
        </div>
      )}

      {/* D.8: per-tab inline notice while a run is in flight */}
      {isRunInFlight && (
        <div className="max-w-3xl mx-auto px-6 pt-6">
          <NonBlockingProgressStrip
            variant="banner"
            title={lang.get('msg.raBuildingRichSchemaRunning')}
            substep={runProgress?.phase !== 'idle' ? runProgress?.phase : undefined}
            current={runProgress?.current}
            total={runProgress?.total}
            startedAt={runProgress?.started_at}
          />
        </div>
      )}

      {isRerunLoading && !isRunInFlight && (
        <div className="max-w-3xl mx-auto px-6 pt-6">
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            {lang.get('msg.analysisInProgressCompare')}
          </div>
        </div>
      )}
      <ComparisonHub
        analysisLanguage={analysisLanguage}
        onReRun={onReRun}
        isRerunLoading={isRerunLoading}
      />
    </>
  );
}

export default CompareTab;
